import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	TrackByFunction,
	ViewChild,
} from '@angular/core';
import {
	AppSettingsService,
	ButtonComponent,
	DatalayerService,
	FocusOnChangeDirective,
	EmbeddedVisibilityDirective,
	NotificationType,
	slugify,
	SvgIconComponent,
} from '@woolworthsnz/styleguide';
import { RecipeCategoryResponse } from '@woolworthsnz/trader-api';
import { RouterLink } from '@angular/router';
import { NgIf, NgClass, NgFor, NgTemplateOutlet, DecimalPipe, I18nPluralPipe } from '@angular/common';

@Component({
	selector: 'recipe-results-header',
	templateUrl: './results-header.component.html',
	styleUrls: ['./results-header.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		EmbeddedVisibilityDirective,
		NgIf,
		FocusOnChangeDirective,
		NgClass,
		NgFor,
		ButtonComponent,
		RouterLink,
		SvgIconComponent,
		NgTemplateOutlet,
		DecimalPipe,
		I18nPluralPipe,
	],
})
export class ResultsHeaderComponent implements AfterViewInit {
	@ViewChild('subCatsArea') subCatsArea: ElementRef;

	@Input() id: number;
	@Input() title: string;
	@Input() description: string;
	@Input() totalRecipes: number;
	@Input() searchMode: boolean;

	subCategoryResponse: RecipeCategoryResponse[];
	displayShowMoreToggle = false;
	showMore = false;

	constructor(
		private cdr: ChangeDetectorRef,
		private datalayerService: DatalayerService,
		private appSettingsService: AppSettingsService
	) {}

	get isEmbeddedInApp(): boolean {
		return this.appSettingsService.getSetting('isEmbeddedInApp');
	}

	get subCategories(): RecipeCategoryResponse[] {
		return this.subCategoryResponse;
	}

	get isSearchQueryVisible(): boolean {
		if (!this.isEmbeddedInApp) {
			return true;
		}

		return this.totalRecipes === 0;
	}

	@Input() set subCategories(allSubCategories: RecipeCategoryResponse[]) {
		// Hide categories with no recipes
		this.subCategoryResponse = allSubCategories.filter((cat) => cat.totalRecipes);
	}

	trackByFn: TrackByFunction<RecipeCategoryResponse> = (_, category) => category.id;

	ngAfterViewInit(): void {
		if (!this.subCatsArea?.nativeElement) {
			return;
		}

		const el = this.subCatsArea.nativeElement;

		// bit of a hack, without this setTimeout the 'show more' panel
		// won't reliably appear when navigating
		setTimeout(() => {
			this.displayShowMoreToggle = el.offsetHeight < el.scrollHeight;
			this.cdr.markForCheck();
		});
	}

	getSubCategoryPath(id: number, title: string): string {
		const categorySlugTitle = slugify(this.title);
		const subCategorySlugTitle = slugify(title);

		return `/recipes/${this.id}/${categorySlugTitle}/${id}/${subCategorySlugTitle}`;
	}

	handleCategoryClick(category: RecipeCategoryResponse): void {
		if (category.description) {
			const slugifiedCategoryDescription = slugify(category.description);
			this.datalayerService.trackRecipeEvent(NotificationType.Button, `RNAV_${slugifiedCategoryDescription}`);
		}
	}
}
